import React from 'react';
import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link as RouterLink, RouteComponentProps } from 'react-router-dom';
// Components
import App from '../../../components/app';
import EnhancedTable from '../../../components/table';
import { TooltipButton } from '../../../components/tooltip-button';
// Modules
import {
    actions as billsActions,
    ProductTypeExceptional,
    selectors as billSelectors,
} from '../../../redux/reducers/bills';
import { tableRequestPoints } from '../../../redux/reducers/table/';
// ressources
import withGlobalStyles from '../../../utils/global-styles';
import UserUtils, { AccountRight, RightRank } from '../../../utils/userUtils';
import { CreditNoteType } from '../../../redux/reducers/api-types';
import { paths } from '../../paths';
import { Cell, createCell } from '../../../components/table/table-cell-creator';
import { clsx } from '../../../utils/htmlUtils';
import { ActionFunction, FormatType, HeadCell } from '../../../local-types';

const queryString = require('query-string');

interface ExceptionalBillProps extends WithTranslation, RouteComponentProps {
    globalClasses: any;
    productTypesExceptional: ProductTypeExceptional[];
    getExceptionalProductTypes: () => void;
}

class ExceptionalBillListPage extends React.Component<ExceptionalBillProps> {
    actions: ActionFunction[];
    tableRef: any;
    filterParams: any;
    canWriteCreditNote: boolean;

    constructor(props: ExceptionalBillProps) {
        super(props);
        this.filterParams = queryString.parse(this.props.location.search);
        this.canWriteCreditNote = UserUtils.checkRightsOrAdmin({
            name: AccountRight.CREDIT_NOTE_MGMT,
            mode: RightRank.W,
        });
    }

    componentDidMount() {
        const { t, getExceptionalProductTypes } = this.props;
        getExceptionalProductTypes();

        const isAdmin = UserUtils.checkRightsOrAdmin({
            name: AccountRight.EXCEPTIONAL_BILLING_MGMT,
            mode: RightRank.W,
        });
        this.actions = [];
        if (isAdmin) {
            this.actions.push({
                label: t('add_exceptional'),
                func: this.handleGoToExceptionalAdd(),
            });
        }
    }

    shouldComponentUpdate(prevProps: ExceptionalBillProps) {
        return prevProps.productTypesExceptional !== this.props.productTypesExceptional;
    }

    handleGoToExceptionalAdd = () => (e) => {
        this.props.history.push(paths.billsExceptionalsAdd);
    };

    headerBuilder = (): HeadCell[] => {
        const { t, productTypesExceptional, globalClasses } = this.props;

        const headers: HeadCell[] = [
            createCell(t, Cell.BILL_NUMBER),
            { ...createCell(t, Cell.BILL_DOCUMENT_PATH), id: 'pdf_invoice_url' },
            createCell(t, Cell.BILL_CREATED_AT),
            {
                id: 'product_type',
                label: t('motif'),
                type: FormatType.Picker,
                pickerOptions: [],
                contains: false,
                formatCell: (row) => {
                    const product: ProductTypeExceptional = productTypesExceptional?.find(
                        (p) => p.name === row.product_type,
                    );
                    return product?.label;
                },
            },
            createCell(t, Cell.USER_ID),
            createCell(t, Cell.COMPANY_ID),
            createCell(t, Cell.AMOUNT_PRE_TAX),
            createCell(t, Cell.AMOUNT_WITH_TAX),
        ];

        if (this.canWriteCreditNote) {
            headers.push({
                id: 'credit',
                label: t('credit.title'),
                filterable: false,
                formatCell: (row) => {
                    return (
                        <TooltipButton title={t('credit.add')}>
                            <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                style={{ marginRight: 8 }}
                                component={RouterLink}
                                to={`${paths.billsCreditNotesAdd}?bill_number=${row.bill_number}&id=${row.id}&type=${CreditNoteType.EXCEPTIONAL}`}
                                className={clsx(globalClasses.btnAutoWidth)}
                            >
                                <Icon fontSize="small">add</Icon>
                            </Button>
                        </TooltipButton>
                    );
                },
            });
        }

        if (productTypesExceptional) {
            const options: any[] = productTypesExceptional.map((r) => {
                return {
                    value: r.name,
                    label: r.label,
                    icon: 'star',
                };
            });
            options.unshift({ value: '', label: '' });
            headers.find((d) => d.id === 'product_type').pickerOptions = options;
        }

        return headers;
    };

    render() {
        const { t } = this.props;

        return (
            <App
                needRights={{ name: AccountRight.EXCEPTIONAL_BILLING_MGMT }}
                disableBreadcrumb
                header={{
                    title: t('title_exceptional'),
                    actions: this.actions,
                }}
            >
                <EnhancedTable
                    onRef={(ref) => (this.tableRef = ref)}
                    headers={this.headerBuilder()}
                    type={tableRequestPoints.BILLS_EXCEPTIONALS}
                    filterParams={this.filterParams}
                    add={paths.billsExceptionalsAdd}
                />
            </App>
        );
    }
}

const ExceptionalBillListPageWithStyleAndTranslation = withGlobalStyles(
    withTranslation(['bill', 'common'])(ExceptionalBillListPage),
);

const mapStateToProps = (state) => ({
    productTypesExceptional: billSelectors.getProductTypesExceptional(state),
});

const mapDispatchToProps = {
    getExceptionalProductTypes: billsActions.getExceptionalProductTypes,
};

export default connect(mapStateToProps, mapDispatchToProps)(ExceptionalBillListPageWithStyleAndTranslation);
